<template>
  <div>
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-6 gap-3">
      <p class="text-muted mb-0">
        Defina com quem os prontuários deste profissional serão compartilhados automaticamente neste local.
        O compartilhamento libera apenas leitura.
        Para equipes TEA/ABA, use regras por especialidade (ABA, fono, TO, psicologia) para que cada profissional veja Sessão, Avaliação e Orientação dos demais.
      </p>
      <select
        v-if="professionals.length > 1"
        class="form-select form-select-solid w-250px"
        v-model="currentProfessionalId"
        @change="loadRules"
      >
        <option v-for="professional in professionals" :key="professional.id_prof" :value="professional.id_prof">
          {{ professional.prof_name }}
        </option>
      </select>
    </div>

    <form class="row g-5 align-items-end mb-10" @submit.prevent="storeRule">
      <div class="col-md-3">
        <label class="form-label required">Tipo</label>
        <select class="form-select form-select-solid" v-model="form.share_target_type" @change="onTypeChange">
          <option value="specialty">Por especialidade</option>
          <option value="council">Por conselho</option>
          <option value="professional">Por profissional</option>
        </select>
      </div>
      <div class="col-md-5" v-if="form.share_target_type === 'specialty'">
        <label class="form-label required">Especialidade</label>
        <select class="form-select form-select-solid" v-model="form.id_spec" required>
          <option value="" disabled>Selecione</option>
          <option v-for="specialty in specialties" :key="specialty.id_spec" :value="specialty.id_spec">
            {{ specialty.spec_name }}
          </option>
        </select>
      </div>
      <div class="col-md-5" v-else-if="form.share_target_type === 'council'">
        <label class="form-label required">Conselho</label>
        <select class="form-select form-select-solid" v-model="form.id_kind" required>
          <option value="" disabled>Selecione</option>
          <option v-for="kind in kinds" :key="kind.id_kind" :value="kind.id_kind">
            {{ kind.kind_acronym }} — {{ kind.kind_name }}
          </option>
        </select>
      </div>
      <div class="col-md-5" v-else>
        <label class="form-label required">Profissional</label>
        <select class="form-select form-select-solid" v-model="form.id_prof_viewer" required>
          <option value="" disabled>Selecione</option>
          <option v-for="professional in viewerProfessionals" :key="professional.id_prof" :value="professional.id_prof">
            {{ professional.prof_name }}
          </option>
        </select>
      </div>
      <div class="col-md-4">
        <button type="submit" class="btn btn-primary" :disabled="saving || !currentProfessionalId">
          <i class="bi bi-plus-lg"></i> Adicionar
        </button>
      </div>
    </form>

    <table class="table table-row-bordered gy-5">
      <thead>
      <tr class="fw-bold fs-6 text-muted">
        <th>Tipo</th>
        <th>Destino</th>
        <th class="text-end">Ações</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="rule in rules" :key="rule.id_medrec_share_rule">
        <td>{{ ruleTypeLabel(rule.share_target_type) }}</td>
        <td>{{ ruleDestination(rule) }}</td>
        <td class="text-end">
          <button class="btn btn-sm btn-light-danger" @click="revokeRule(rule)" title="Revogar">
            <i class="bi bi-trash"></i> Revogar
          </button>
        </td>
      </tr>
      <tr v-if="rules.length === 0">
        <td colspan="3" class="text-center text-muted py-10">
          Nenhuma regra de compartilhamento cadastrada.
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";

export default {
  name: "MedicalRecordSettingsTabShare",
  data() {
    return {
      professionals: [],
      specialties: [],
      kinds: [],
      rules: [],
      currentProfessionalId: '',
      saving: false,
      form: {
        share_target_type: 'specialty',
        id_spec: '',
        id_prof_viewer: '',
        id_kind: '',
      },
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    userAuth() {
      return this.$store.state.auth.user;
    },
    viewerProfessionals() {
      return this.professionals.filter((p) => Number(p.id_prof) !== Number(this.currentProfessionalId));
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      await Promise.all([this.loadProfessionals(), this.loadSpecialties(), this.loadKinds()]);
      if (this.userAuth?.id_prof) {
        this.currentProfessionalId = this.userAuth.id_prof;
      } else if (this.professionals.length) {
        this.currentProfessionalId = this.professionals[0].id_prof;
      }
      this.loadRules();
    },
    loadProfessionals() {
      return http.get(`/places/${this.place.id_place}/professionals`).then((response) => {
        this.professionals = response.data || [];
      });
    },
    loadSpecialties() {
      return http.get(`/places/${this.place.id_place}/specialties`).then((response) => {
        this.specialties = response.data || [];
      });
    },
    loadKinds() {
      return http.get('/kinds').then((response) => {
        this.kinds = response.data || [];
      });
    },
    loadRules() {
      if (!this.currentProfessionalId) {
        this.rules = [];
        return;
      }
      return http.get(`/places/${this.place.id_place}/professionals/${this.currentProfessionalId}/medical-record-share-rules`)
        .then((response) => {
          this.rules = response.data || [];
        });
    },
    onTypeChange() {
      this.form.id_spec = '';
      this.form.id_prof_viewer = '';
      this.form.id_kind = '';
    },
    ruleTypeLabel(type) {
      if (type === 'specialty') {
        return 'Especialidade';
      }
      if (type === 'council') {
        return 'Conselho';
      }
      return 'Profissional';
    },
    ruleDestination(rule) {
      if (rule.share_target_type === 'specialty') {
        return rule.specialty?.spec_name || '-';
      }
      if (rule.share_target_type === 'council') {
        if (!rule.kind) {
          return '-';
        }
        return `${rule.kind.kind_acronym} — ${rule.kind.kind_name}`;
      }
      return rule.viewer?.prof_name || '-';
    },
    storeRule() {
      if (!this.currentProfessionalId) {
        return;
      }
      this.saving = true;
      const payload = {
        share_target_type: this.form.share_target_type,
        id_spec: this.form.share_target_type === 'specialty' ? this.form.id_spec : null,
        id_prof_viewer: this.form.share_target_type === 'professional' ? this.form.id_prof_viewer : null,
        id_kind: this.form.share_target_type === 'council' ? this.form.id_kind : null,
      };
      http.post(`/places/${this.place.id_place}/professionals/${this.currentProfessionalId}/medical-record-share-rules`, payload)
        .then(() => {
          Alert.toastSuccess('Compartilhamento adicionado.');
          this.onTypeChange();
          this.loadRules();
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Erro ao salvar.');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    revokeRule(rule) {
      Swal.fire({
        title: 'Revogar compartilhamento?',
        text: 'Os profissionais deixarão de ver os prontuários por esta regra.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, revogar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary',
        },
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }
        http.delete(`/places/${this.place.id_place}/professionals/${this.currentProfessionalId}/medical-record-share-rules/${rule.id_medrec_share_rule}`)
          .then(() => {
            Alert.toastSuccess('Compartilhamento revogado.');
            this.loadRules();
          })
          .catch((e) => {
            Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Erro ao revogar.');
          });
      });
    },
  },
};
</script>
